import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { WordSubmission } from '../components/sections/word-submission'
import type { WordSubmissionFormData } from '../types/word-submission'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { trackWordSubmission, trackError } from '../lib/analytics'

export function Submit() {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [categories, setCategories] = useState<any[]>([])
  const [existingWords, setExistingWords] = useState<any[]>([])

  // Fetch categories and existing words from Supabase
  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      try {
        // Fetch categories
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('categories')
          .select('*')
          .order('name')

        if (categoriesError) throw categoriesError

        // Fetch existing foreign words for duplicate checking
        const { data: wordsData, error: wordsError } = await supabase
          .from('foreign_words')
          .select(`
            id,
            word,
            word_arabic,
            category_id,
            submitted_by,
            submitted_at
          `)
          .order('submitted_at', { ascending: false })

        if (wordsError) throw wordsError

        setCategories(categoriesData || [])
        setExistingWords(wordsData || [])
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleSubmit = async (formData: WordSubmissionFormData) => {
    if (!user) {
      toast.error('يرجى تسجيل الدخول لإرسال كلمة')
      return
    }

    try {
      // Note: User profile is auto-created by database trigger on signup
      const { data: wordId, error } = await supabase
        .rpc('submit_word_with_alternative', {
          p_word: formData.foreignWord,
          p_word_arabic: formData.arabicTransliteration,
          p_category_id: formData.categoryId,
          p_context: formData.usageContext,
          p_reasoning: formData.reasoning,
          p_submitted_by: user.id,
          p_egyptian_word: formData.egyptianWord,
          p_explanation: formData.explanation,
        })

      if (error) throw error

      if (!wordId) {
        throw new Error('Failed to get inserted word ID')
      }

      toast.success('تم إرسال الكلمة والبديل بنجاح! يمكنك الآن اقتراح بدائل إضافية.')
      
      // Track successful word submission
      trackWordSubmission(wordId.toString())
      
      // Navigate to the alternatives page for this word
      navigate(`/word/${wordId}/alternatives`)
    } catch (error: any) {
      console.error('Error submitting word:', error)
      
      // Track error
      trackError(error.message || 'Error submitting word', 'word_submission')
      
      if (error.code === '23505') {
        // Duplicate key error
        toast.error('هذه الكلمة موجودة بالفعل')
      } else {
        toast.error('حدث خطأ أثناء إرسال الكلمة')
      }
    }
  }

  const handleSearchExisting = (query: string) => {
    console.log('Searching for:', query)
    // Component handles filtering locally
  }

  // Map categories to component format
  const mappedCategories = categories.map(cat => ({
    id: cat.id,
    name: cat.name,
    nameEnglish: cat.name_english,
  }))

  // Map existing words to component format
  const mappedExistingWords = existingWords.map(word => ({
    id: word.id,
    foreignWord: word.word,
    arabicTransliteration: word.word_arabic,
    categoryId: word.category_id,
    submittedBy: word.submitted_by,
    submittedAt: word.submitted_at,
  }))

  // Map current user to component format
  const currentUser = user ? {
    id: user.id,
    name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
    avatarUrl: user.user_metadata?.avatar_url,
  } : undefined

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-slate-400">جاري التحميل...</div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900">
        <div className="text-slate-400">يرجى تسجيل الدخول لإرسال كلمة</div>
      </div>
    )
  }

  return (
    <WordSubmission
      categories={mappedCategories}
      existingWords={mappedExistingWords}
      currentUser={currentUser || { id: '', name: 'Guest', avatarUrl: null }}
      onSubmit={handleSubmit}
      onSearchExisting={handleSearchExisting}
    />
  )
}
